/**
 * FleetQueryPanel — the body of a Fleet Query top-level tab.
 *
 * Unlike a regular query tab (pinned to ONE connection, living inside a
 * connection tab), a fleet tab is a top-level tab — a sibling of dual-pane
 * tabs — because it routes each statement to a connection via the
 * `[connection]` prefix and therefore spans the whole fleet.
 *
 * Each statement is parsed for its `[connection]` routing prefix
 * (lib/sqlroute.js, the client mirror of internal/bulksql/parse.go), resolved
 * to a connection, and run on a lazily created per-connection session
 * (Map<connId, sessionId>). Outcomes stream into a fleet message log that
 * reuses <MessageRow> with a connection chip. Statements that return rows
 * publish to a per-connection result tab (one tab per connection, the
 * connection name IS the label; last result set on that connection wins).
 *
 * Since shipped: per-connection transactions (auto-BEGIN + commit/rollback
 * strip), the safe-update override modal, query :params (bound on every
 * participating connection), cancel/stop, and routed-connection autocomplete
 * (`[Conn].db.` → tables, `[Conn].db.table.` → columns via bracketComplete).
 * NOT YET: inline result editing in the fleet result grids.
 *
 * Props:
 *   tab        — persisted record { id, title, sql, defaultConnectionId }
 *   active     — true while this tab is the visible top-level tab
 *   onSqlChange(sql)            — persist editor content (Shell debounces saves)
 *   onRename(title)            — persist a custom title (unused this iteration)
 *   onDefaultConnChange(connId) — persist the default-connection choice
 */
import { createSignal, createResource, createEffect, createMemo, onCleanup, onMount, For, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { splitStatements, splitScript, statementAtCursor } from '../lib/sqlsplit.js';
import { isExpandStatement, expandOne } from '../lib/sqlexpand.js';
import { extractStatementName } from '../lib/sqlname.js';
import { parseStatement } from '../lib/sqlroute.js';
import { listFleetPresets, saveFleetPreset, deleteFleetPreset } from '../lib/fleetpresets.js';
import { extractParams, extractWriteTarget, coerceParam, paramInputType } from '../lib/sqlparams.js';
import { formatNumberWithSep, relTime } from '../lib/format.js';
import { copyText } from '../lib/export.js';
import { currentUser } from '../lib/auth-store.js';
import { listSavedQueries, createSavedQuery, updateSavedQuery, deleteSavedQuery } from '../lib/savedqueries.js';
import { listMyHistory } from '../lib/history.js';
import { outlineItems } from '../lib/sqloutline.js';
import { identAt } from '../lib/findusages.js';
import {
  createQuerySession, closeQuerySession, closeQuerySessionBeacon, executeQuery,
  beginTransaction, commitTransaction, rollbackTransaction, cancelQuery, setSessionVars,
} from '../lib/querysession.js';
import {
  isReadOnlySQL, implicitCommitKind,
  loadCompletionSchema, completionState, buildSqlSchema, aliasForTable,
} from '../lib/schema.js';
import CodeMirrorSQL, { formatEditor } from '../components/CodeMirrorSQL.jsx';
import FormatMenu from '../components/FormatMenu.jsx';
import MessageRow from '../components/MessageRow.jsx';
import Grid from '../components/Grid.jsx';
import ContextMenu from '../components/ContextMenu.jsx';
import { commaPasteMenuItems } from '../lib/pastecomma.js';
import Modal from '../components/Modal.jsx';
import QueryDslHelp from '../components/QueryDslHelp.jsx';
import FindUsagesModal from '../components/FindUsagesModal.jsx';
import ColumnExpandModal from '../components/ColumnExpandModal.jsx';
import PasteTemplateModal from '../components/PasteTemplateModal.jsx';
import {
  editorAutoFormat, setEditorAutoFormat,
  editorLineWrap, setEditorLineWrap,
  gridThousandSep, gridShowTypes,
  queryTxByDefault, querySafeMode,
  querySidebarVisible, querySidebarWidth, setQuerySidebarWidth,
} from '../lib/prefs.js';

export default function FleetQueryPanel(props) {
  // Editor content — seeded once from the persisted snapshot, then owned here
  // and pushed back up (debounced) so reload restores it.
  const [sqlText, setSqlText] = createSignal(props.tab?.sql || '');
  const [running, setRunning] = createSignal(false);
  // Stop/cancel: stopRequested halts the run loop before the next statement;
  // currentExecSid lets Stop KILL the in-flight statement on its session.
  const [stopRequested, setStopRequested] = createSignal(false);
  let currentExecSid = null;
  // Auto-BEGIN preference — wrap each connection's writes in a transaction.
  // Seeded from the SAME pref the query tabs use, so the user's choice carries.
  const [txToggle, setTxToggle] = createSignal(queryTxByDefault());
  let editorView = null;

  // Report editor content upward (debounced). Shell's updateFleetTabSql no-op
  // guard absorbs the initial restore value, so mounting won't dirty the snapshot.
  let contentTimer = null;
  createEffect(() => {
    const text = sqlText();
    if (!props.onSqlChange) return;
    if (contentTimer) clearTimeout(contentTimer);
    contentTimer = setTimeout(() => props.onSqlChange(text), 500);
  });
  onCleanup(() => { if (contentTimer) clearTimeout(contentTimer); });

  // Fleet routing needs every visible connection (for the default-conn picker
  // AND the `[connection]` autocomplete). One fetch per panel; cheap.
  const [conns] = createResource(async () => {
    try { return (await api.get('/api/connections')).connections || []; }
    catch { return []; }
  });
  // Case-insensitive name lookup, matching the Go parser's strings.EqualFold.
  const findConn = (name) => (conns() || []).find((c) => c.name.toLowerCase() === String(name).toLowerCase());
  const defaultConn = () => {
    const id = props.tab?.defaultConnectionId || 0;
    return id ? (conns() || []).find((c) => c.id === id) : null;
  };

  // ── Mode: 'free' (route via [connection] prefix) | 'preset' (run on a set of
  // checked connection+database targets). Seeded from the persisted tab and
  // pushed back up on change.
  const [mode, setModeSig] = createSignal(props.tab?.mode === 'preset' ? 'preset' : 'free');
  const isPreset = () => mode() === 'preset';
  const setMode = (m) => { const v = m === 'preset' ? 'preset' : 'free'; setModeSig(v); props.onModeChange?.(v); };

  // Preset working set: [{ connectionId, database }]. Pushed up (persisted) on change.
  const [presets, setPresetsSig] = createSignal(Array.isArray(props.tab?.presets) ? props.tab.presets : []);
  const setPresets = (arr) => { const v = Array.isArray(arr) ? arr : []; setPresetsSig(v); props.onPresetsChange?.(v); };
  const hasPair = (cid, db) => presets().some((p) => p.connectionId === cid && p.database === db);
  const togglePair = (cid, db) => setPresets(
    hasPair(cid, db)
      ? presets().filter((p) => !(p.connectionId === cid && p.database === db))
      : [...presets(), { connectionId: cid, database: db }]);
  const removePair = (cid, db) => setPresets(presets().filter((p) => !(p.connectionId === cid && p.database === db)));
  const countForConn = (cid) => presets().filter((p) => p.connectionId === cid).length;

  // A "target" is the unit a run touches. Free mode resolves one per routed
  // connection (db = the session's no-db default); preset mode has one per
  // checked (connection, database) pair. All runtime maps (sessions, txTargets,
  // resultTabs, the message chip) key by `target.key`.
  //   free:   { key: String(connId),     conn, database: null, label: conn.name }
  //   preset: { key: connId + ':' + db,  conn, database: db,   label: "Conn / db" }
  const freeTarget = (conn) => ({ key: String(conn.id), conn, database: null, label: conn.name });
  // Preset targets, resolved against visible connections (stale pairs skipped).
  const presetTargets = createMemo(() => {
    const out = [];
    for (const p of presets()) {
      const c = (conns() || []).find((x) => x.id === p.connectionId);
      if (!c) continue; // resolve-and-skip: connection no longer visible
      out.push({ key: `${c.id}:${p.database}`, conn: c, database: p.database, label: `${c.name} / ${p.database}` });
    }
    return out;
  });
  // The connection whose schema drives autocomplete/lint: the default connection
  // in free mode, the first checked target in preset mode (so the plain editor
  // gets unqualified completion against that db).
  const primaryTarget = () => presetTargets()[0] || null;
  const schemaConn = () => (isPreset() ? (primaryTarget()?.conn || null) : defaultConn());
  const schemaDb = () => (isPreset() ? (primaryTarget()?.database || '') : '');

  // ── Autocomplete schema (default connection) ──────────────────────────────
  // Fleet statements name tables as `db.table` (no current DB), so the lang-sql
  // namespace comes from the DEFAULT connection with no defaultSchema — fully
  // qualified `db.table.col` completion works; unqualified stays keyword-only.
  // Statements routed to OTHER connections complete through the bracket path
  // (bracketComplete: `[Conn].` → databases → tables → columns, fetched against
  // the ROUTED connection). Known asymmetry: the NON-bracket sources (unqualified
  // WHERE columns, bare `db.table`, the linter) still resolve against the default
  // connection's schema — one lang-sql namespace per editor. Benign for the fleet
  // use case (same-schema databases), so a per-statement namespace swap isn't
  // worth its complexity today.
  createEffect(() => { const c = schemaConn(); if (c) loadCompletionSchema(c.id); });
  const sqlSchema = createMemo(() => {
    const c = schemaConn();
    if (!c) return null;
    const st = completionState(c.id)();
    // Preset mode pins the editor to the primary target's db, so unqualified
    // completion resolves against it; free mode keeps no defaultSchema (fully
    // qualified db.table completion only).
    return { schema: buildSqlSchema(st.databases), defaultSchema: (isPreset() ? (schemaDb() || undefined) : undefined), routines: [] };
  });

  // ── Per-connection sessions ────────────────────────────────────────────────
  // A fleet run touches several connections; each gets ONE pinned session,
  // created lazily on first use and reused for the rest of the run (and future
  // runs in this tab). Closed on unmount / page unload.
  const sessions = new Map(); // target.key -> sessionId
  const getSession = async (target) => {
    if (sessions.has(target.key)) return sessions.get(target.key);
    // Preset targets pin the session to their database (server runs USE `db` and
    // enforces the user's per-db access); free targets open with no current DB.
    const r = await createQuerySession(target.conn.id, target.database || null);
    sessions.set(target.key, r.sessionId);
    return r.sessionId;
  };
  // Run a session-bound call; if the backend idle-reaped the session (HTTP 404),
  // drop the cached id, reopen on the same connection, and retry ONCE. Safe:
  // a 404 means the statement never reached MySQL, and the reaper never evicts
  // an in-transaction session — so nothing uncommitted is lost. (Mirrors
  // QueryPanel.withReconnect, but keyed per connection.) A session lost with a
  // transaction open anyway (server restart, dropped connection) took that work
  // with it: clear the stale in-tx mark (so the next run auto-BEGINs again) and
  // FAIL instead of retrying — the retry would run on a fresh autocommit
  // session, committing this statement on its own, detached from the lost work.
  const withReconnect = async (target, action) => {
    try {
      return await action(await getSession(target));
    } catch (e) {
      if (e?.status !== 404) throw e;
      sessions.delete(target.key);
      if (txTargets[target.key]) {
        markTx(target, false);
        throw new Error(`Session on ${target.label} was lost with a transaction open — its uncommitted changes were rolled back, and this statement was not run. Re-run from the start.`);
      }
      logInfo(`Session on ${target.label} expired while idle — reconnected on a fresh connection.`);
      return await action(await getSession(target));
    }
  };
  onCleanup(() => { for (const sid of sessions.values()) closeQuerySession(sid); });

  // ── Fleet message log ──────────────────────────────────────────────────────
  const [messages, setMessages] = createStore([]);
  let msgSeq = 0;
  const logInfo  = (text) => setMessages((m) => [...m, { kind: 'info', text, at: Date.now() }]);
  const logOK    = (text) => setMessages((m) => [...m, { kind: 'ok', text, at: Date.now() }]);
  const logError = (text) => setMessages((m) => [...m, { kind: 'error', text, at: Date.now() }]);
  const clearMessages = () => setMessages([]);
  // pushStatement(sql, name?, connName?) — connName drives the connection chip.
  const pushStatement = (sql, name, connName) => {
    const id = ++msgSeq;
    setMessages((m) => [...m, {
      id, kind: 'statement', sql, name: name || undefined, conn: connName || undefined,
      status: 'running', at: Date.now(),
    }]);
    return id;
  };
  const updateStatement = (id, patch) => {
    const idx = messages.findIndex((m) => m.id === id);
    if (idx >= 0) setMessages(idx, (m) => ({ ...m, ...patch }));
  };

  // nowTick drives the elapsed timer on a running statement (4 Hz while running).
  const [nowTick, setNowTick] = createSignal(Date.now());
  createEffect(() => {
    if (!running()) return;
    const h = setInterval(() => setNowTick(Date.now()), 250);
    onCleanup(() => clearInterval(h));
  });
  // Per-statement "⚠ N warnings" expand state, by message id.
  const [warnOpen, setWarnOpen] = createSignal(new Set());
  const toggleWarn = (id) => setWarnOpen((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  // ── Per-target transactions ─────────────────────────────────────────────────
  // A fleet run touches several targets, each with its OWN session and therefore
  // its OWN independent transaction. So the regular query tab's single `inTx`
  // boolean becomes a per-target map: txTargets[target.key] = target.label when
  // that target has an open (uncommitted) transaction. Auto-BEGIN happens lazily
  // before the first write to each target; the server's `r.inTransaction` keeps
  // the map honest through implicit commits (DDL etc.).
  const [txTargets, setTxTargets] = createStore({}); // { [target.key]: target.label }
  // Setting a store key to undefined leaves it enumerable (Solid keeps the key),
  // so filter out the committed/rolled-back entries rather than relying on delete.
  // Memoized — read in several render spots (badge strip, Commit-all gate, the
  // editor in-tx tint) so it shouldn't rebuild the list per read.
  const openTxTargets = createMemo(() => Object.entries(txTargets)
    .filter(([, label]) => label != null)
    .map(([key, label]) => ({ key, label })));
  const anyTxOpen = () => openTxTargets().length > 0;
  const markTx = (target, open) => {
    if (open) setTxTargets(target.key, target.label);
    else setTxTargets(target.key, undefined); // delete the key
  };

  const commitTx = async (key, label) => {
    const sid = sessions.get(key);
    if (!sid) { setTxTargets(key, undefined); return; }
    try {
      await commitTransaction(sid);
      setTxTargets(key, undefined);
      logOK(`COMMIT on ${label}`);
    } catch (e) {
      if (e?.status === 404) { setTxTargets(key, undefined); logInfo(`Transaction on ${label} already ended — the session was closed.`); return; }
      logError(`COMMIT on ${label} failed: ${e.message}`);
    }
  };
  const rollbackTx = async (key, label) => {
    const sid = sessions.get(key);
    if (!sid) { setTxTargets(key, undefined); return; }
    try {
      await rollbackTransaction(sid);
      setTxTargets(key, undefined);
      logInfo(`ROLLBACK on ${label}`);
    } catch (e) {
      if (e?.status === 404) { setTxTargets(key, undefined); logInfo(`Transaction on ${label} already ended — the session was closed.`); return; }
      logError(`ROLLBACK on ${label} failed: ${e.message}`);
    }
  };
  // Independent targets — commit/roll back in parallel (each swallows its own
  // errors, so Promise.all never rejects). The mapped array is a snapshot, taken
  // before any store mutation invalidates the memo.
  const commitAll = async () => { await Promise.all(openTxTargets().map((t) => commitTx(t.key, t.label))); };
  const rollbackAll = async () => { await Promise.all(openTxTargets().map((t) => rollbackTx(t.key, t.label))); };

  // Guard an accidental refresh/close while a query is running or any
  // transaction is open — the native browser prompt lets the user cancel.
  // pagehide additionally closes sessions on a real unload (rolls back any open
  // tx server-side + releases locks).
  onMount(() => {
    const onBeforeUnload = (e) => {
      if (running() || anyTxOpen()) { e.preventDefault(); e.returnValue = ''; }
    };
    const onUnload = () => { for (const sid of sessions.values()) closeQuerySessionBeacon(sid); };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onUnload);
    onCleanup(() => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onUnload);
    });
  });

  // ── Per-connection result tabs ─────────────────────────────────────────────
  // One tab per connection that returns rows; the connection name IS the label.
  // Last result set on a connection wins (v1 — a connection that runs several
  // SELECTs shows the most recent grid). 'messages' is the always-present tab.
  const [resultTabs, setResultTabs] = createStore([]); // { key, label, columns, rows, truncated, sql, durationMs }
  const [activeResultTab, setActiveResultTab] = createSignal('messages');
  const activeResult = () => resultTabs.find((t) => t.key === activeResultTab());
  const publishResult = (target, set, sql, durationMs) => {
    const entry = {
      key: target.key, label: target.label,
      columns: set.columns || [], rows: set.rows || [],
      truncated: !!set.truncated, sql, durationMs,
    };
    const idx = resultTabs.findIndex((t) => t.key === target.key);
    if (idx >= 0) setResultTabs(idx, entry);
    else setResultTabs((t) => [...t, entry]);
  };

  // Editor autocomplete for the routing prefix: `[` → connection names,
  // `[Conn].` → databases, `[Conn].db.` → that database's tables,
  // `[Conn].db.table.` → that table's columns. Each level is fetched on demand
  // against the ROUTED connection (not the default one) and cached, so fleet
  // autocomplete works across the whole fleet — not just the default connection.
  // Supplying this prop is what enables `[connection]` completion; regular query
  // tabs omit it, so they never offer it (the gate IS the data).
  const dbCache = {};      // connId → [db]
  const tableCache = {};   // `${connId}\t${db}` → [table]
  const columnCache = {};  // `${connId}\t${db}\t${table}` → [column]
  const bracketComplete = {
    connections: () => conns() || [],
    databases: async (name) => {
      const c = findConn(name);
      if (!c) return [];
      if (dbCache[c.id]) return dbCache[c.id];
      try {
        const r = await api.get(`/api/connections/${c.id}/databases`);
        dbCache[c.id] = (r.databases || []).map((d) => d.name).filter(Boolean);
        return dbCache[c.id];
      } catch { return []; }
    },
    tables: async (name, db) => {
      const c = findConn(name);
      if (!c || !db) return [];
      const key = `${c.id}\t${db}`;
      if (tableCache[key]) return tableCache[key];
      try {
        const r = await api.get(`/api/connections/${c.id}/databases/${encodeURIComponent(db)}/objects`);
        tableCache[key] = (r.tables || []).map((t) => t.name).filter(Boolean);
        return tableCache[key];
      } catch { return []; }
    },
    columns: async (name, db, table) => {
      const c = findConn(name);
      if (!c || !db || !table) return [];
      const key = `${c.id}\t${db}\t${table}`;
      if (columnCache[key]) return columnCache[key];
      try {
        const r = await api.get(`/api/connections/${c.id}/databases/${encodeURIComponent(db)}/tables/${encodeURIComponent(table)}/columns`);
        columnCache[key] = (r.columns || []).map((col) => col.name).filter(Boolean);
        return columnCache[key];
      } catch { return []; }
    },
  };

  // ── Run ────────────────────────────────────────────────────────────────────
  // Run one already-resolved item on its connection's session. connWrites is
  // true when this connection receives at least one write in the run (so its
  // reads join the same transaction); a pure-read connection is never wrapped.
  const runOne = async ({ target, name, sql, connWrites, safeUpdates }) => {
    // Auto-BEGIN: open a transaction on this target before its first write, when
    // the toggle is on and it isn't already in one. (Per-target mirror of the
    // regular query tab's auto-BEGIN.)
    if (txToggle() && connWrites && !txTargets[target.key]) {
      try {
        await withReconnect(target, (sid) => beginTransaction(sid));
        markTx(target, true);
        logInfo(`BEGIN on ${target.label}`);
      } catch (e) {
        logError(`BEGIN on ${target.label} failed: ${e.message}`);
        return false;
      }
    }

    const msgId = pushStatement(sql, name, target.label);
    try {
      // Track the in-flight session so Stop can KILL exactly this statement.
      const r = await withReconnect(target, (sid) => { currentExecSid = sid; return executeQuery(sid, sql, safeUpdates); });
      // Server truth keeps the per-target tx badge honest (an implicit commit
      // from DDL, or a server-side auto-rollback, flips inTransaction).
      markTx(target, !!r.inTransaction);
      if (r.autoRolledBack) {
        updateStatement(msgId, { status: 'error', result: `Failed: ${r.error}`, durationMs: r.durationMs });
        logInfo(`Transaction on ${target.label} auto-ROLLBACK on error`);
        return false;
      }
      if (r.error) {
        updateStatement(msgId, { status: 'error', result: `Error: ${r.error}`, durationMs: r.durationMs });
        return false;
      }
      const sets = r.resultSets || [];
      let summary;
      if (sets.length > 0) {
        // Last result set wins for this target's tab.
        publishResult(target, sets[sets.length - 1], sql, r.durationMs);
        const total = sets.reduce((acc, s) => acc + (s.rows || []).length, 0);
        summary = sets.length === 1
          ? `${formatNumberWithSep(total)} row${total === 1 ? '' : 's'} returned${sets[0].truncated ? ' (truncated)' : ''}`
          : `${sets.length} result sets — ${formatNumberWithSep(total)} row${total === 1 ? '' : 's'} total`;
      } else {
        summary = `OK — ${r.rowsAffected} row${r.rowsAffected === 1 ? '' : 's'} affected${r.lastInsertId ? ` (last id ${r.lastInsertId})` : ''}`;
      }
      updateStatement(msgId, { status: 'ok', result: summary, durationMs: r.durationMs, warnings: r.warnings });
      return true;
    } catch (e) {
      updateStatement(msgId, { status: 'error', result: e.message });
      return false;
    } finally {
      currentExecSid = null;
    }
  };

  // Stop the run: halt the loop before the next statement, and KILL the one
  // currently in flight (its executeQuery then returns an error → the loop
  // breaks). Open transactions are LEFT open — the user decides commit/rollback,
  // same as the regular query tab.
  const stopQuery = async () => {
    if (!running()) return;
    setStopRequested(true);
    logInfo('Stop requested — cancelling the running statement and halting the run.');
    const sid = currentExecSid;
    if (sid) {
      try { await cancelQuery(sid); }
      catch (e) { toast.error('Cancel failed: ' + e.message); }
    }
  };

  // ── Editor right-click menu + companions ──────────────────────────────────
  // Ports the regular query tab's editor context menu: result-title annotation,
  // copy-columns (resolved from the default connection's schema cache), find
  // usages, and the statement-scoped / whole-doc format actions.
  const [editorCtxMenu, setEditorCtxMenu] = createSignal(null); // { x, y, items }
  const [titlePrompt, setTitlePrompt] = createSignal(null); // { stmtFrom, stmtTo, existingName, draft }
  let titleInputRef;

  // Statement-scoped formats momentarily select the statement so formatEditor's
  // selection-aware path operates on it; whole-doc formats clear the selection.
  const formatRange = (view, from, to, mode) => {
    view.dispatch({ selection: { anchor: from, head: to } });
    formatEditor(view, mode);
  };
  const formatAll = (view, mode) => {
    const head = view.state.selection.main.head;
    view.dispatch({ selection: { anchor: head, head } });
    formatEditor(view, mode);
  };

  // Resolve a table/view reference at the given doc position from the DEFAULT
  // connection's cached schema. Supports a `db.tbl` qualifier; with no current
  // DB the unqualified fallback scans every DB on the default connection.
  const resolveTableAt = (text, pos) => {
    const c = defaultConn();
    if (!c) return null;
    const isId = (ch) => ch === '_' || ch === '$' ||
      (ch >= '0' && ch <= '9') || (ch >= 'A' && ch <= 'Z') || (ch >= 'a' && ch <= 'z');
    let start = pos;
    while (start > 0 && isId(text[start - 1])) start--;
    let end = pos;
    while (end < text.length && isId(text[end])) end++;
    if (start === end) return null;
    const name = text.slice(start, end);
    let schema = '';
    if (start >= 2 && text[start - 1] === '.') {
      let qEnd = start - 1, qStart = qEnd;
      while (qStart > 0 && isId(text[qStart - 1])) qStart--;
      if (qStart < qEnd) schema = text.slice(qStart, qEnd);
    }
    const dbs = completionState(c.id)().databases || [];
    const wantLower = name.toLowerCase();
    const findIn = (db) => {
      if (!db) return null;
      const t = (db.tables || []).find((x) => x.name.toLowerCase() === wantLower)
            || (db.views  || []).find((x) => x.name.toLowerCase() === wantLower);
      return t ? { name: t.name, columns: t.columns || [], database: db.database } : null;
    };
    if (schema) return findIn(dbs.find((d) => d.database.toLowerCase() === schema.toLowerCase()));
    for (const db of dbs) {
      const hit = findIn(db);
      if (hit) return hit;
    }
    return null;
  };

  const onEditorContextMenu = (e, view) => {
    if (!view) return;
    let pos = view.posAtCoords({ x: e.clientX, y: e.clientY });
    if (pos == null) pos = view.state.selection.main.head;

    // Identifier at the clicked position (selection wins when it covers it).
    const sel = view.state.selection.main;
    const selCovers = sel.from !== sel.to && pos >= sel.from && pos <= sel.to;
    const ident = selCovers
      ? view.state.sliceDoc(sel.from, sel.to).trim()
      : identAt(sqlText(), pos);

    const stmt = statementAtCursor(sqlText(), pos);
    const lineText = view.state.doc.lineAt(pos).text;
    const onWhitespace = !lineText.trim();

    const items = [];
    if (stmt) {
      const existing = extractStatementName(stmt.text);
      items.push({
        label: existing.name ? `🏷 Rename result title… (${existing.name})` : '🏷 Add result title…',
        onClick: () => openTitlePrompt(stmt, existing.name || ''),
      });
    }
    const table = ident ? resolveTableAt(sqlText(), pos) : null;
    if (table && table.columns.length > 0) {
      // Open the column-expander preselected to this table (same as Ctrl+Shift+E).
      items.push({
        label: `⛶ Expand columns of ${table.name}…`,
        onClick: () => setExpandReq({
          statementText: stmt ? stmt.text : '',
          fromTables: [{ db: table.database, table: table.name, alias: aliasForTable(stmt ? stmt.text : '', table.name, table.database) }],
          selectedText: table.name,
          insert: (text) => {
            const p = view.state.selection.main.head;
            view.dispatch({ changes: { from: p, insert: text }, selection: { anchor: p + text.length } });
            view.focus();
          },
        }),
      });
    }
    if (ident && defaultConn()) {
      items.push({
        label: `🔍 Find usages of "${ident.length > 28 ? ident.slice(0, 28) + '…' : ident}"`,
        onClick: () => openFindUsages(ident),
      });
    }
    if (items.length === 0 && defaultConn()) {
      items.push({ label: '🔍 Find usages…', onClick: () => openFindUsages('') });
    }

    // Statement-scoped format inside a statement; whole-document only on whitespace.
    if (items.length > 0) items.push({ divider: true });
    if (stmt && !onWhitespace) {
      items.push({ label: '⌗ Format statement (full)', onClick: () => formatRange(view, stmt.start, stmt.end, 'full') });
      items.push({ label: '↔ Single-line statement', onClick: () => formatRange(view, stmt.start, stmt.end, 'oneline') });
      items.push({ label: '⏷ Change case in statement', onClick: () => formatRange(view, stmt.start, stmt.end, 'case') });
      items.push({ label: '↳ Indent statement (routine body)', onClick: () => formatRange(view, stmt.start, stmt.end, 'indent') });
    } else {
      items.push({ label: '⌗ Format all statements', onClick: () => formatAll(view, 'full') });
      items.push({ label: '↔ Single-line all statements', onClick: () => formatAll(view, 'oneline') });
      items.push({ label: '⏷ Change case in all', onClick: () => formatAll(view, 'case') });
      items.push({ label: '↳ Indent routine bodies', onClick: () => formatAll(view, 'indent') });
    }
    // Paste newline-delimited clipboard text as a comma list (plain or quoted).
    items.push({ divider: true }, ...commaPasteMenuItems(view));
    items.push({
      label: '📋 Paste with template…',
      onClick: () => setPasteTemplateView(view),
    });
    setEditorCtxMenu({ x: e.clientX, y: e.clientY, items });
  };

  // Paste-with-template modal: holds the editor view to insert into on submit.
  const [pasteTemplateView, setPasteTemplateView] = createSignal(null);
  const onPasteTemplateConfirm = (text) => {
    const v = pasteTemplateView();
    if (!v || !text) return;
    v.dispatch(v.state.replaceSelection(text));
    v.focus();
  };

  // Title-prompt: INSERT a `-- @name: <title>` line above the statement, or
  // REPLACE the existing annotation. (Same mechanics as the regular tab; in
  // fleet the name shows as a chip on the statement's message row.)
  const openTitlePrompt = (stmt, existingName) => {
    setTitlePrompt({ stmtFrom: stmt.start, stmtTo: stmt.end, existingName, draft: existingName });
    queueMicrotask(() => { titleInputRef?.focus(); titleInputRef?.select?.(); });
  };
  const submitTitlePrompt = () => {
    const p = titlePrompt();
    if (!p || !editorView) { setTitlePrompt(null); return; }
    const newName = (p.draft || '').trim();
    const stmtText = editorView.state.sliceDoc(p.stmtFrom, p.stmtTo);
    const stripped = extractStatementName(stmtText);
    const replacement = newName === '' ? stripped.sql : `-- @name: ${newName}\n${stripped.sql}`;
    editorView.dispatch({ changes: { from: p.stmtFrom, to: p.stmtTo, insert: replacement } });
    setTitlePrompt(null);
    editorView.focus();
  };

  // ── Find usages ────────────────────────────────────────────────────────────
  // Searches the DEFAULT connection (the modal needs one connection to route
  // the backend search). "Open" destinations load the SQL into THIS editor —
  // a fleet tab has no sibling query tabs to open into.
  const [findOpen, setFindOpen] = createSignal(false);
  const [findInitial, setFindInitial] = createSignal('');
  const openFindUsages = (preFillIdent) => {
    if (!defaultConn()) { toast.error('Pick a default connection first — Find usages searches one connection.'); return; }
    if (preFillIdent != null) setFindInitial(preFillIdent);
    else {
      const sel = editorView?.state?.selection?.main;
      const selected = sel && sel.from !== sel.to ? editorView.state.sliceDoc(sel.from, sel.to).trim() : '';
      setFindInitial(selected || identAt(sqlText(), sel?.head ?? 0) || '');
    }
    setFindOpen(true);
  };
  const appendSql = (sql, label) => {
    if (!sql) return;
    const cur = sqlText();
    setSqlText(cur.trim() ? cur.replace(/\s*$/, '') + '\n\n' + sql : sql);
    toast.success(`Loaded ${label || 'SQL'} into the fleet editor`);
  };

  // ── Ctrl+Shift+E column expander ───────────────────────────────────────────
  // Resolves columns from the default connection's schema cache; the request's
  // fromTables carry their own `db.` qualifiers so no current DB is needed.
  const [expandReq, setExpandReq] = createSignal(null);

  // 📖 DSL — the query-DSL/editor-extras help modal (expand, :params, snippets).
  const [dslHelpOpen, setDslHelpOpen] = createSignal(false);

  // ── Parameterized queries (:name / :name#type#) ───────────────────────────
  // promptParams shows the modal and resolves with a { name: value } map (or
  // null if cancelled). Last-entered values are remembered per name for this
  // tab session so re-runs pre-fill. A NULL checkbox binds SQL NULL.
  // (Mirrors QueryPanel; the fleet twist is that the values are bound — via
  // SET @name = ? — on EVERY connection participating in the run.)
  let paramMemory = {};
  const [paramPrompt, setParamPrompt] = createSignal(null); // { params, values, nulls }
  let paramResolve = null;
  const promptParams = (params) => new Promise((resolve) => {
    paramResolve = resolve;
    const values = {}, nulls = {};
    for (const p of params) { values[p.name] = paramMemory[p.name] ?? ''; nulls[p.name] = false; }
    setParamPrompt({ params, values, nulls });
  });
  const setParamValue = (name, v) => setParamPrompt((pp) => ({ ...pp, values: { ...pp.values, [name]: v } }));
  const setParamNull  = (name, v) => setParamPrompt((pp) => ({ ...pp, nulls:  { ...pp.nulls,  [name]: v } }));
  const submitParams = () => {
    const pp = paramPrompt();
    if (!pp) return;
    const out = {};
    for (const p of pp.params) {
      if (pp.nulls[p.name]) { out[p.name] = null; paramMemory[p.name] = ''; continue; }
      out[p.name] = coerceParam(pp.values[p.name], p.type);
      paramMemory[p.name] = pp.values[p.name];
    }
    setParamPrompt(null);
    paramResolve?.(out);
    paramResolve = null;
  };
  const cancelParams = () => { setParamPrompt(null); paramResolve?.(null); paramResolve = null; };

  // ── No-WHERE write guard ──────────────────────────────────────────────────
  // confirmUnscoped lists UPDATE/DELETE statements that have no top-level WHERE
  // or LIMIT (would touch every row on their target connection) and resolves
  // true (run anyway) / false. The fleet guard only implements the no-WHERE
  // tier: Lax-key's key-column check needs the connection's schema cache + a
  // current DB, and fleet sessions have neither (statements use qualified
  // db.table names) — so lax-key behaves like lax-any here. Strict mode's
  // server-side SQL_SAFE_UPDATES flag remains the authoritative guard.
  const [unsafePrompt, setUnsafePrompt] = createSignal(null); // { items: [{kind, sql, conn}] }
  let unsafeResolve = null;
  const confirmUnscoped = (items) => new Promise((resolve) => {
    unsafeResolve = resolve;
    setUnsafePrompt({ items });
  });
  const resolveUnsafe = (ok) => { setUnsafePrompt(null); unsafeResolve?.(ok); unsafeResolve = null; };

  // ── Implicit-commit guard ─────────────────────────────────────────────────
  // confirmImplicitCommit warns that DDL / BEGIN / LOCK / account statements
  // will implicitly COMMIT the open transaction on their target connection
  // (MySQL semantics, irreversible) and resolves true / false.
  const [commitPrompt, setCommitPrompt] = createSignal(null); // { items: [{kind, sql, conn}] }
  let commitResolve = null;
  const confirmImplicitCommit = (items) => new Promise((resolve) => {
    commitResolve = resolve;
    setCommitPrompt({ items });
  });
  const resolveCommit = (ok) => { setCommitPrompt(null); commitResolve?.(ok); commitResolve = null; };

  // Source to run: the selection if there is one, else the whole editor.
  const runSource = () => {
    if (editorView) {
      const sel = editorView.state.selection.main;
      if (sel.from !== sel.to) return editorView.state.sliceDoc(sel.from, sel.to);
    }
    return sqlText();
  };

  const runStatements = async (stmts) => {
    if (running()) return;
    if (!stmts.length) { logInfo('Nothing to run.'); return; }

    // Parse + resolve every statement up front — fail fast so a routing typo
    // never half-runs a multi-server batch. (Mirrors the Go batch's validate
    // pass.) Each statement: strip [connection] prefix, strip ~name~/@name,
    // resolve the target connection (prefix → that conn, else the default).
    const plan = [];
    const errors = []; // { sql, error }
    for (const s of stmts) {
      let parsed;
      try { parsed = parseStatement(s.text); }
      catch (e) { errors.push({ sql: s.text.trim(), error: e.message }); continue; }
      const { name, sql: finalSql } = extractStatementName(parsed.rewritten);
      let conn;
      if (parsed.conn) {
        conn = findConn(parsed.conn);
        if (!conn) { errors.push({ sql: finalSql.trim(), error: `Unknown connection [${parsed.conn}] — not in your visible connections.` }); continue; }
      } else {
        conn = defaultConn();
        if (!conn) { errors.push({ sql: finalSql.trim(), error: 'No [connection] prefix and no default connection selected.' }); continue; }
      }
      const target = freeTarget(conn);
      // `expand src="…", target="…&Col…"` runs src on THIS statement's resolved
      // connection (so its results reflect that server's own data) and replaces
      // itself with one real statement per src row, before any of the write-
      // detection / param / safe-update passes below see it.
      if (isExpandStatement(finalSql)) {
        try {
          const generated = await expandOne(finalSql, async (sql) => {
            const r = await withReconnect(target, (sid) => executeQuery(sid, sql, false));
            return { columns: r.resultSets?.[0]?.columns, rows: r.resultSets?.[0]?.rows, error: r.error };
          });
          for (const text of generated) plan.push({ conn, name, sql: text, target });
        } catch (e) {
          errors.push({ sql: finalSql.trim(), error: e.message });
        }
        continue;
      }
      plan.push({ conn, name, sql: finalSql, target });
    }

    // A connection is "write-touched" if ANY statement routed to it isn't a pure
    // read — then ALL its statements (incl. reads) run inside one transaction, so
    // a read sees the run's own uncommitted writes. Pure-read connections aren't
    // wrapped (nothing to commit). Mirrors the regular tab's run-level decision,
    // generalized per connection.
    const writeConnIds = new Set(plan.filter((p) => !isReadOnlySQL(p.sql)).map((p) => p.conn.id));
    for (const p of plan) p.connWrites = writeConnIds.has(p.conn.id);

    if (errors.length) {
      for (const e of errors) {
        const id = pushStatement(e.sql);
        updateStatement(id, { status: 'error', result: `Error: ${e.error}` });
      }
      toast.error(`${errors.length} statement${errors.length === 1 ? '' : 's'} couldn't be routed — nothing ran.`);
      return;
    }

    // ── :name parameters — prompt once, bind on every participating connection.
    // Each connection gets the SAME values (SET @name = ?, injection-safe); the
    // statements then read the rewritten @name form.
    const { params } = extractParams(plan.map((p) => p.sql).join('\n;\n'));
    if (params.length > 0) {
      const values = await promptParams(params);
      if (values === null) return; // cancelled — nothing ran
      const uniqueTargets = [...new Map(plan.map((p) => [p.target.key, p.target])).values()];
      try {
        await Promise.all(uniqueTargets.map((t) => withReconnect(t, (sid) => setSessionVars(sid, values))));
      } catch (e) {
        logError('Failed to set parameters: ' + e.message);
        return;
      }
      for (const p of plan) p.sql = extractParams(p.sql).rewritten;
    }

    // ── Safe-update guard (client tier) — block bare UPDATE/DELETEs that would
    // touch every row on their target connection. LIMIT scopes a write; 'off'
    // skips the check. Strict mode additionally sends the server flag below.
    const mode = querySafeMode(); // 'strict' | 'lax-key' | 'lax-any' | 'off'
    const isStrict = mode === 'strict';
    let overrideIdxs = null; // Set<i> of statements the user confirmed past the guard
    if (mode !== 'off') {
      const blocked = [];
      for (let i = 0; i < plan.length; i++) {
        const t = extractWriteTarget(plan[i].sql);
        if (!t.kind || t.hasLimit || t.hasWhere) continue;
        blocked.push({ i, kind: t.kind, sql: plan[i].sql, conn: plan[i].conn.name });
      }
      if (blocked.length > 0) {
        const ok = await confirmUnscoped(blocked);
        if (!ok) return; // cancelled — nothing ran
        overrideIdxs = new Set(blocked.map((b) => b.i));
      }
    }
    // Server-side SQL_SAFE_UPDATES only in 'strict' mode, and only when the
    // statement wasn't user-overridden. Lax modes send 0 (client modal was the
    // guard); 'off' is always 0. (Same flag decision as the regular tab.)
    plan.forEach((p, i) => { p.safeUpdates = isStrict && !(overrideIdxs && overrideIdxs.has(i)); });

    // ── Implicit-commit guard — warn before any statement that MySQL would
    // implicitly COMMIT its target connection's open transaction for (DDL,
    // BEGIN, LOCK TABLES, account/maintenance statements). The commit happens
    // BEFORE the statement and can't be rolled back — without this the
    // per-connection "uncommitted" badge would silently lie.
    const committing = plan
      .map((p) => (txTargets[p.target.key] ? { kind: implicitCommitKind(p.sql), sql: p.sql, conn: p.conn.name } : null))
      .filter((x) => x && x.kind);
    if (committing.length > 0) {
      const ok = await confirmImplicitCommit(committing);
      if (!ok) return; // cancelled — nothing ran
    }

    setActiveResultTab('messages'); // watch statements stream during the run
    setStopRequested(false);
    setRunning(true);
    try {
      // Document order, stop on first error (safe default across servers).
      for (const item of plan) {
        // Stop clicked between statements — halt before starting the next one.
        if (stopRequested()) { logInfo('Stopped by user — remaining statements were not run.'); break; }
        const ok = await runOne(item);
        if (!ok) { logInfo(stopRequested() ? 'Stopped by user — remaining statements were not run.' : 'Stopped on error — remaining statements were not run.'); break; }
      }
    } finally {
      setRunning(false);
    }

    // Surface the first target (in plan order) that produced rows.
    const planKeys = [...new Set(plan.map((p) => p.target.key))];
    const firstWithResult = planKeys.find((k) => resultTabs.some((t) => t.key === k));
    if (firstWithResult != null) setActiveResultTab(firstWithResult);
  };

  // ── Run (preset mode) ────────────────────────────────────────────────────────
  // Every statement runs on every checked (connection, database) target, target
  // by target. Per-target resilient: a statement error stops THAT target's
  // remaining statements but the run continues with the other targets. The
  // [connection] routing prefix is forbidden here — a statement carrying one
  // blocks the whole run (nothing runs).
  const runStatementsPreset = async (stmts) => {
    if (running()) return;
    const targets = presetTargets();
    if (!targets.length) {
      logInfo('No targets — check at least one connection + database in the Targets picker.');
      toast.error('Pick at least one target (connection + database).');
      return;
    }
    if (!stmts.length) { logInfo('Nothing to run.'); return; }

    // Parse each statement: strip ~name~/@name; FORBID any [connection] prefix.
    const items = []; // { name, sql, idx }
    const errors = []; // { sql, error }
    let idx = 0;
    for (const s of stmts) {
      let parsed;
      try { parsed = parseStatement(s.text); }
      catch (e) { errors.push({ sql: s.text.trim(), error: e.message }); continue; }
      if (parsed.conn) {
        errors.push({ sql: s.text.trim(), error: `Routing prefix [${parsed.conn}] isn't allowed in Connection-preset mode — pick targets above instead.` });
        continue;
      }
      const { name, sql } = extractStatementName(parsed.rewritten);
      items.push({ name, sql, idx: idx++ });
    }
    if (errors.length) {
      for (const e of errors) {
        const id = pushStatement(e.sql);
        updateStatement(id, { status: 'error', result: `Error: ${e.error}` });
      }
      toast.error(`${errors.length} statement${errors.length === 1 ? '' : 's'} blocked — nothing ran.`);
      return;
    }
    if (!items.length) { logInfo('Nothing to run.'); return; }

    // Every statement hits every target, so if the script has ANY write, every
    // target is write-touched (its reads join the run's own transaction).
    const anyWrite = items.some((it) => !isReadOnlySQL(it.sql));

    // ── :name parameters — prompt once; bound per target inside the loop (so a
    // target that can't bind fails alone instead of aborting the whole run).
    const { params } = extractParams(items.map((it) => it.sql).join('\n;\n'));
    let paramValues = null;
    let runItems = items;
    if (params.length > 0) {
      paramValues = await promptParams(params);
      if (paramValues === null) return; // cancelled — nothing ran
      runItems = items.map((it) => ({ ...it, sql: extractParams(it.sql).rewritten }));
    }

    // ── Safe-update guard — a bare UPDATE/DELETE runs on EVERY target, so warn
    // once per offending statement (overridden statements skip the server flag).
    const smode = querySafeMode();
    const isStrict = smode === 'strict';
    let overrideStmt = null; // Set<idx>
    if (smode !== 'off') {
      const blocked = [];
      for (const it of runItems) {
        const t = extractWriteTarget(it.sql);
        if (!t.kind || t.hasLimit || t.hasWhere) continue;
        blocked.push({ i: it.idx, kind: t.kind, sql: it.sql, conn: `all ${targets.length} target${targets.length === 1 ? '' : 's'}` });
      }
      if (blocked.length > 0) {
        const ok = await confirmUnscoped(blocked);
        if (!ok) return;
        overrideStmt = new Set(blocked.map((b) => b.i));
      }
    }
    const safeUpdatesFor = (i) => isStrict && !(overrideStmt && overrideStmt.has(i));

    // ── Implicit-commit guard — for any target that already has an open tx (from
    // a prior run), warn before a statement that MySQL would implicitly commit.
    const committing = [];
    for (const t of targets) {
      if (!txTargets[t.key]) continue;
      for (const it of runItems) {
        const kind = implicitCommitKind(it.sql);
        if (kind) committing.push({ kind, sql: it.sql, conn: t.label });
      }
    }
    if (committing.length > 0) {
      const ok = await confirmImplicitCommit(committing);
      if (!ok) return;
    }

    setActiveResultTab('messages');
    setStopRequested(false);
    setRunning(true);
    let okCount = 0, failCount = 0;
    try {
      outer:
      for (const t of targets) {
        if (stopRequested()) { logInfo('Stopped by user — remaining targets were not run.'); break; }
        logInfo(`▶ ${t.label}`);
        // Bind params on this target (a failure here fails just this target).
        if (paramValues) {
          try { await withReconnect(t, (sid) => setSessionVars(sid, paramValues)); }
          catch (e) { logError(`Failed to set parameters on ${t.label}: ${e.message}`); failCount++; continue; }
        }
        // Expand any `expand` items against THIS target's own data, so each
        // checked branch generates its statements from its own rows (a per-branch
        // process, not one branch's rows applied everywhere). A src-query failure
        // fails just this target. (The pre-flight safe-update/implicit-commit
        // guards ran on the opaque `expand` wrapper, not these generated
        // statements — they're user-templated and run inside this target's
        // transaction, with the strict-mode server flag still applied per row.)
        let itemsForTarget;
        try {
          itemsForTarget = [];
          for (const it of runItems) {
            if (!isExpandStatement(it.sql)) { itemsForTarget.push(it); continue; }
            const generated = await expandOne(it.sql, async (sql) => {
              const r = await withReconnect(t, (sid) => executeQuery(sid, sql, false));
              return { columns: r.resultSets?.[0]?.columns, rows: r.resultSets?.[0]?.rows, error: r.error };
            });
            for (const text of generated) itemsForTarget.push({ ...it, sql: text });
          }
        } catch (e) {
          logError(`${t.label}: ${e.message}`);
          failCount++;
          continue;
        }
        let targetOk = true;
        for (const it of itemsForTarget) {
          if (stopRequested()) { logInfo('Stopped by user — remaining statements were not run.'); break outer; }
          const ok = await runOne({ target: t, name: it.name, sql: it.sql, connWrites: anyWrite, safeUpdates: safeUpdatesFor(it.idx) });
          if (!ok) { targetOk = false; logInfo(`Stopped on error on ${t.label} — its remaining statements were skipped; continuing with other targets.`); break; }
        }
        if (targetOk) okCount++; else failCount++;
      }
    } finally {
      setRunning(false);
    }
    logInfo(`Done — ${okCount} target${okCount === 1 ? '' : 's'} OK${failCount ? `, ${failCount} failed` : ''}.`);

    // Surface the first target (in run order) that produced rows.
    const firstWithResult = targets.map((t) => t.key).find((k) => resultTabs.some((rt) => rt.key === k));
    if (firstWithResult != null) setActiveResultTab(firstWithResult);
  };

  // Mode-aware dispatch for the run entry points. running() only turns on after
  // the awaited prep (expand queries, the :param prompt, the guard confirms), so
  // a second Run in that window would pass its check and run the batch twice —
  // `dispatching` latches from entry instead.
  let dispatching = false;
  const dispatchRun = async (stmts) => {
    if (running() || dispatching) return;
    dispatching = true;
    try { await (isPreset() ? runStatementsPreset(stmts) : runStatements(stmts)); }
    finally { dispatching = false; }
  };

  // ── Targets picker (preset mode) ───────────────────────────────────────────
  // A popover listing connections; expand one to lazily load its databases and
  // check (connection, database) pairs. dbsByConn caches the db list per conn.
  const [pickerOpen, setPickerOpen] = createSignal(false);
  const [expandedConn, setExpandedConn] = createSignal(null); // connId currently expanded
  const [dbsByConn, setDbsByConn] = createStore({});           // connId -> [dbName]
  const [dbsLoading, setDbsLoading] = createSignal(null);      // connId being fetched
  const loadDbs = async (c) => {
    if (dbsByConn[c.id]) return;
    setDbsLoading(c.id);
    try {
      const r = await api.get(`/api/connections/${c.id}/databases`);
      setDbsByConn(c.id, (r.databases || []).map((d) => d.name).filter(Boolean));
    } catch { setDbsByConn(c.id, []); }
    finally { setDbsLoading(null); }
  };
  const toggleExpand = (c) => {
    if (expandedConn() === c.id) { setExpandedConn(null); return; }
    setExpandedConn(c.id);
    loadDbs(c);
  };
  let targetsPickerEl;
  onMount(() => {
    const onDown = (e) => { if (targetsPickerEl && !targetsPickerEl.contains(e.target)) setPickerOpen(false); };
    document.addEventListener('mousedown', onDown);
    onCleanup(() => document.removeEventListener('mousedown', onDown));
  });

  // ── Named, reusable presets (server-side, per-user) ────────────────────────
  // A preset is a saved set of (connection, database) pairs; loading one checks
  // its pairs into the working set (stale pairs — connection since removed — are
  // resolve-and-skipped). Saving stores only currently-resolved pairs (no orphans).
  const [savedPresets, { refetch: refetchPresets }] = createResource(async () => {
    try { return await listFleetPresets(); } catch { return []; }
  });
  const [presetMenuOpen, setPresetMenuOpen] = createSignal(false);
  let presetMenuEl;
  onMount(() => {
    const onDown = (e) => { if (presetMenuEl && !presetMenuEl.contains(e.target)) setPresetMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    onCleanup(() => document.removeEventListener('mousedown', onDown));
  });
  const applyPreset = (preset) => {
    const pairs = preset.pairs || [];
    const known = new Set((conns() || []).map((c) => c.id));
    const kept = pairs.filter((p) => known.has(p.connectionId));
    const skipped = pairs.length - kept.length;
    setPresets(kept.map((p) => ({ connectionId: p.connectionId, database: p.database })));
    setPresetMenuOpen(false);
    if (skipped > 0) {
      toast.error(`Loaded "${preset.name}" — ${skipped} pair${skipped === 1 ? '' : 's'} skipped (connection no longer exists).`);
    } else {
      toast.success(`Loaded preset "${preset.name}" (${kept.length} target${kept.length === 1 ? '' : 's'}).`);
    }
  };
  const saveCurrentPreset = async () => {
    // Save only resolved pairs, so a stale orphan never gets persisted.
    const pairs = presetTargets().map((t) => ({ connectionId: t.conn.id, database: t.database }));
    if (!pairs.length) { toast.error('No targets checked — nothing to save.'); return; }
    const name = window.prompt('Save the checked targets as a preset named:');
    if (name == null) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await saveFleetPreset(trimmed, pairs);
      await refetchPresets();
      setPresetMenuOpen(false);
      toast.success(`Saved preset "${trimmed}".`);
    } catch (e) { toast.error('Save failed: ' + (e?.message || e)); }
  };
  const removePreset = async (preset) => {
    if (!window.confirm(`Delete preset "${preset.name}"?`)) return;
    try {
      await deleteFleetPreset(preset.id);
      await refetchPresets();
      toast.success(`Deleted preset "${preset.name}".`);
    } catch (e) { toast.error('Delete failed: ' + (e?.message || e)); }
  };

  // Run entry points — same trio as the regular tab. Run-all/script honour an
  // editor selection (run just the selected statements); run-at-cursor takes
  // the statement under the caret.
  const runAll = async () => dispatchRun(splitStatements(runSource()));
  const runScript = async () => dispatchRun(splitScript(runSource()));
  const runAtCursor = async () => {
    if (running()) return;
    const cursor = editorView?.state?.selection?.main?.head ?? sqlText().length;
    const stmt = statementAtCursor(sqlText(), cursor);
    if (!stmt) { logInfo('No statement under cursor.'); return; }
    await dispatchRun([stmt]);
  };

  // ── Saved queries / history / outline sidebar (parity with query tabs) ──────
  // Saved queries + recall history are per-user GLOBAL lists (not connection-
  // scoped), so a fleet tab reuses the exact same lists, libs, and styles the
  // query tabs use. Loading drops the SQL into THIS editor; saving lands the
  // fleet query in the SAME saved-queries list. (Scrap is query-tab-only — it
  // recovers closed query tabs — so it's omitted here.)
  const [sidebarVisible, setSidebarVisible] = createSignal(querySidebarVisible());
  const [sidebarTab, setSidebarTab] = createSignal('saved');
  const [savedList, { refetch: refetchSaved }] = createResource(listSavedQueries);
  const [historyList, { refetch: refetchHistory }] = createResource(() => listMyHistory(60));
  const [savedFilter, setSavedFilter] = createSignal('');
  const [historyFilter, setHistoryFilter] = createSignal('');
  const mine = (q) => q.ownerUserId === currentUser()?.id;
  const visibleSaved = () => {
    const list = savedList() || [];
    const f = savedFilter().trim().toLowerCase();
    if (!f) return list;
    return list.filter((q) => q.title.toLowerCase().includes(f) || (q.description || '').toLowerCase().includes(f));
  };
  const visibleHistory = () => {
    const list = historyList() ?? [];
    const f = historyFilter().trim().toLowerCase();
    if (!f) return list;
    return list.filter((h) => (h.sqlText || '').toLowerCase().includes(f) || (h.database || '').toLowerCase().includes(f));
  };
  const outline = createMemo(() => outlineItems(sqlText()));
  const jumpToOutline = (item) => {
    if (!editorView) return;
    editorView.dispatch({ selection: { anchor: item.start }, scrollIntoView: true });
    editorView.focus();
  };
  const fleetFirstLine = (s) => {
    const line = (s || '').split('\n').find((l) => l.trim()) || '';
    return line.length > 80 ? line.slice(0, 80) + '…' : line;
  };

  // Load into the editor: replace when empty, else prompt append/replace.
  const [loadPrompt, setLoadPrompt] = createSignal(null); // { sql, label }
  const applyLoad = (sql, mode) => {
    if (mode === 'append') setSqlText(sqlText() ? sqlText().replace(/\s*$/, '') + '\n\n' + sql : sql);
    else setSqlText(sql);
  };
  const requestLoad = (sql, label) => {
    if (!sqlText().trim()) { applyLoad(sql, 'replace'); logInfo(`Loaded ${label}`); return; }
    setLoadPrompt({ sql, label });
  };
  const loadSaved = (q) => requestLoad(q.sqlText, `saved query "${q.title}"`);
  const loadHistory = (h) => requestLoad(h.sqlText, 'history entry');

  // Save dialog — same shape (title / description / source-db + tag sharing) as
  // the query tab's, so a fleet query saves into the shared saved-queries list.
  const [savingDlg, setSavingDlg] = createSignal(null);
  const [tagOptions] = createResource(
    () => (savingDlg()?.shared ? 'load' : null),
    async () => { try { return (await api.get('/api/user-tags')).tags || []; } catch { return []; } },
  );
  const toggleShareTag = (t) => {
    const dlg = savingDlg();
    if (!dlg) return;
    const has = (dlg.shareTags || []).includes(t);
    setSavingDlg({ ...dlg, shareTags: has ? dlg.shareTags.filter((x) => x !== t) : [...(dlg.shareTags || []), t] });
  };
  const openSaveDialog = () => {
    if (!sqlText().trim()) { toast.error('Nothing to save — editor is empty.'); return; }
    setSavingDlg({ title: '', description: '', sourceDatabase: schemaDb() || '', sqlText: sqlText(), editingId: null, shared: false, shareTags: [] });
  };
  const closeSaveDialog = () => setSavingDlg(null);
  const commitSave = async () => {
    const dlg = savingDlg();
    if (!dlg || !dlg.title.trim()) { toast.error('Title required.'); return; }
    if (!dlg.sqlText.trim()) { toast.error('Query content is empty.'); return; }
    if (dlg.shared && (dlg.shareTags || []).length === 0) { toast.error('Pick at least one tag to share with (or turn sharing off).'); return; }
    try {
      const payload = {
        title: dlg.title.trim(), description: dlg.description.trim(),
        sourceDatabase: dlg.sourceDatabase || '', sqlText: dlg.sqlText,
        shareTags: dlg.shared ? (dlg.shareTags || []) : [],
      };
      if (dlg.editingId) await updateSavedQuery(dlg.editingId, payload);
      else await createSavedQuery(payload);
      refetchSaved();
      closeSaveDialog();
      toast.success(dlg.editingId ? 'Query updated' : 'Query saved');
    } catch (e) { toast.error(e.message); }
  };
  const renameSaved = (q) => setSavingDlg({
    title: q.title, description: q.description, sourceDatabase: q.sourceDatabase,
    sqlText: q.sqlText, editingId: q.id, shared: (q.shareTags || []).length > 0, shareTags: q.shareTags || [],
  });
  const removeSaved = async (q) => {
    if (!confirm(`Delete saved query "${q.title}"?`)) return;
    try { await deleteSavedQuery(q.id); refetchSaved(); toast.success('Deleted'); }
    catch (e) { toast.error(e.message); }
  };

  // Sidebar resize splitter (shares the query-tab width pref).
  const onSidebarDrag = (e) => {
    e.preventDefault();
    const startX = e.clientX, startW = querySidebarWidth();
    const move = (ev) => setQuerySidebarWidth(startW + (ev.clientX - startX));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.cursor = ''; document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  let rootEl;
  // Keyboard shortcuts (Ctrl+R run-at-cursor, Ctrl+Shift+R run-all). Scoped to the
  // visible panel: active-tab gating alone isn't enough — fleet tabs are kept
  // mounted, and one in a non-focused outer tab can still be "active", so we also
  // require this panel to be on screen (offsetParent !== null ⇒ not display:none).
  onMount(() => {
    const onKey = (e) => {
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl || (e.key !== 'r' && e.key !== 'R')) return;
      if (!props.active || !rootEl || rootEl.offsetParent === null) return;
      e.preventDefault();
      if (e.shiftKey) runAll();
      else            runAtCursor();
    };
    document.addEventListener('keydown', onKey);
    onCleanup(() => document.removeEventListener('keydown', onKey));
  });

  const onInput = (v) => setSqlText(v);

  return (
    <div class="fleet-panel" ref={rootEl}>
      <div class="fleet-body">
      <Show when={sidebarVisible()}>
        <aside class="query-sidebar" style={`width: ${querySidebarWidth()}px;`}>
          <div class="query-sidebar-header">
            <div class="query-sidebar-tabs">
              <button class="query-sidebar-tab" classList={{ active: sidebarTab() === 'saved' }} onClick={() => setSidebarTab('saved')}>Saved</button>
              <button class="query-sidebar-tab" classList={{ active: sidebarTab() === 'history' }} onClick={() => { setSidebarTab('history'); refetchHistory(); }}>History</button>
              <button class="query-sidebar-tab" classList={{ active: sidebarTab() === 'outline' }} onClick={() => setSidebarTab('outline')} title="Jump between the statements in this editor">Outline</button>
            </div>
            <Show when={sidebarTab() === 'saved'}>
              <div class="query-sidebar-save-row">
                <button class="btn btn-sm btn-ghost" onClick={openSaveDialog} title="Save current editor as a named query">+ Save</button>
              </div>
            </Show>
          </div>

          {/* Saved queries */}
          <Show when={sidebarTab() === 'saved'}>
            <input class="input objects-filter-input" style="margin:6px 8px;width:auto;" placeholder="Filter…"
                   value={savedFilter()} onInput={(e) => setSavedFilter(e.target.value)} />
            <div class="query-saved-list">
              <Show when={!savedList.loading} fallback={<div class="muted" style="padding:8px 10px;">Loading…</div>}>
                <Show when={visibleSaved().length === 0}><div class="muted" style="padding:8px 10px;font-size:12px;">No saved queries yet.</div></Show>
                <For each={visibleSaved()}>
                  {(q) => (
                    <div class="query-saved-item" onDblClick={() => loadSaved(q)}>
                      <div class="query-saved-title" onClick={() => loadSaved(q)} title="Click to load">
                        {q.title}
                        <Show when={(q.shareTags || []).length}><span class="sq-shared-badge" title={`Shared with: ${q.shareTags.join(', ')}`}>🔗</span></Show>
                      </div>
                      <Show when={q.description}><div class="query-saved-desc">{q.description}</div></Show>
                      <Show when={!mine(q)}><div class="query-saved-author muted">shared by {q.authorName || 'another user'}</div></Show>
                      <Show when={q.sourceDatabase}><div class="query-saved-db mono">{q.sourceDatabase}</div></Show>
                      <Show when={mine(q)}>
                        <div class="query-saved-actions">
                          <button class="btn-link" onClick={() => renameSaved(q)}>edit</button>
                          <button class="btn-link btn-link-danger" onClick={() => removeSaved(q)}>delete</button>
                        </div>
                      </Show>
                    </div>
                  )}
                </For>
              </Show>
            </div>
          </Show>

          {/* Recall history — your query/fleet statements; click to load */}
          <Show when={sidebarTab() === 'history'}>
            <input class="input objects-filter-input" style="margin:6px 8px;width:auto;" placeholder="Search history…"
                   value={historyFilter()} onInput={(e) => setHistoryFilter(e.target.value)} />
            <div class="query-saved-list">
              <Show when={!historyList.loading} fallback={<div class="muted" style="padding:8px 10px;">Loading…</div>}>
                <Show when={(historyList() ?? []).length === 0}><div class="muted" style="padding:8px 10px;font-size:12px;">No history yet — run a query.</div></Show>
                <Show when={(historyList() ?? []).length > 0 && visibleHistory().length === 0}><div class="muted" style="padding:8px 10px;font-size:12px;">No history matches “{historyFilter()}”.</div></Show>
                <For each={visibleHistory()}>
                  {(h) => (
                    <div class="query-history-item" onClick={() => loadHistory(h)} title={h.sqlText}>
                      <div class="query-history-sql mono">{fleetFirstLine(h.sqlText)}</div>
                      <div class="query-history-meta">
                        <span class={h.success ? 'hist-ok' : 'hist-err'}>{h.success ? '✓' : '✕'}</span>
                        <Show when={h.database}><span class="mono muted">{h.database}</span></Show>
                        <span class="muted">{relTime(h.createdAt)}</span>
                      </div>
                    </div>
                  )}
                </For>
              </Show>
            </div>
          </Show>

          {/* Outline — statements in this editor; click to jump the caret */}
          <Show when={sidebarTab() === 'outline'}>
            <div class="query-saved-list">
              <Show when={outline().length === 0}><div class="muted" style="padding:8px 10px;font-size:12px;">No statements yet.</div></Show>
              <For each={outline()}>
                {(item, i) => (
                  <div class="query-outline-item" onClick={() => jumpToOutline(item)} title="Jump to this statement">
                    <span class="query-outline-num mono">{i() + 1}</span>
                    <span class="query-outline-label mono">{item.label}</span>
                  </div>
                )}
              </For>
            </div>
          </Show>
        </aside>
        <div class="query-splitter" onPointerDown={onSidebarDrag} title="Drag to resize sidebar" />
      </Show>
      <div class="query-main">
        <div class="query-toolbar">
          <button class="btn btn-sm btn-ghost" title="Toggle the saved queries / history sidebar"
                  onClick={() => setSidebarVisible((v) => !v)}>☰</button>
          {/* Mode: Free (route via [connection] prefix) vs Connection preset
              (run every statement on a checked set of connection+database targets). */}
          <div class="fleet-mode-toggle" title="Free: prefix each statement with [connection] to route it.&#10;Connection preset: run every statement on a checked set of connection + database targets.">
            <button class="btn btn-sm" classList={{ 'btn-primary': !isPreset(), 'btn-ghost': isPreset() }}
                    disabled={running()} onClick={() => setMode('free')}>Free</button>
            <button class="btn btn-sm" classList={{ 'btn-primary': isPreset(), 'btn-ghost': !isPreset() }}
                    disabled={running()} onClick={() => setMode('preset')}>Preset</button>
          </div>

          <Show when={!isPreset()}>
            <label class="query-tool-label">Default conn:</label>
            <select
              class="input query-conn-select"
              value={props.tab?.defaultConnectionId || 0}
              onChange={(e) => props.onDefaultConnChange?.(Number(e.target.value))}
              disabled={running()}
              title="Statements with no [connection] prefix run on this connection. Choose “none” to require every statement to be prefixed."
            >
              <option value="0">— none (every statement must be prefixed) —</option>
              <For each={conns() || []}>
                {(c) => <option value={c.id} selected={c.id === (props.tab?.defaultConnectionId || 0)}>{c.name}</option>}
              </For>
            </select>
          </Show>

          <Show when={isPreset()}>
            <div class="fleet-targets" ref={targetsPickerEl}>
              <button class="btn btn-sm" disabled={running()} onClick={() => setPickerOpen((o) => !o)}
                      title="Pick the connection + database targets every statement runs on">
                🎯 Targets ({presetTargets().length}) ▾
              </button>
              <Show when={pickerOpen()}>
                <div class="fleet-targets-pop">
                  <div class="fleet-targets-head">
                    <span>Connection + database targets</span>
                    <Show when={presets().length > 0}>
                      <button class="btn btn-sm btn-ghost" onClick={() => setPresets([])}>Clear all</button>
                    </Show>
                  </div>
                  <div class="fleet-targets-list">
                    <Show when={(conns() || []).length === 0}><div class="muted" style="padding:8px;">No connections.</div></Show>
                    <For each={conns() || []}>
                      {(c) => (
                        <div class="fleet-targets-conn">
                          <button class="fleet-targets-connbtn" onClick={() => toggleExpand(c)}>
                            <span class="fleet-targets-caret">{expandedConn() === c.id ? '▾' : '▸'}</span>
                            <span class="fleet-targets-connname">{c.name}</span>
                            <Show when={countForConn(c.id) > 0}><span class="fleet-targets-count">{countForConn(c.id)}</span></Show>
                          </button>
                          <Show when={expandedConn() === c.id}>
                            <div class="fleet-targets-dbs">
                              <Show when={dbsLoading() === c.id}><div class="muted" style="padding:4px 8px;">Loading…</div></Show>
                              <For each={dbsByConn[c.id] || []}>
                                {(db) => (
                                  <label class="checkbox-label fleet-targets-db">
                                    <input type="checkbox" checked={hasPair(c.id, db)} onChange={() => togglePair(c.id, db)} />
                                    {db}
                                  </label>
                                )}
                              </For>
                              <Show when={dbsLoading() !== c.id && (dbsByConn[c.id] || []).length === 0}>
                                <div class="muted" style="padding:4px 8px;">no databases</div>
                              </Show>
                            </div>
                          </Show>
                        </div>
                      )}
                    </For>
                  </div>
                </div>
              </Show>
            </div>

            {/* Named presets — save the checked targets, or load a saved set. */}
            <div class="fleet-targets" ref={presetMenuEl}>
              <button class="btn btn-sm btn-ghost" disabled={running()} onClick={() => setPresetMenuOpen((o) => !o)}
                      title="Save the checked targets as a reusable named preset, or load one">
                💾 Presets ▾
              </button>
              <Show when={presetMenuOpen()}>
                <div class="fleet-targets-pop">
                  <div class="fleet-targets-head">
                    <span>Saved presets</span>
                    <button class="btn btn-sm" onClick={saveCurrentPreset}>💾 Save current…</button>
                  </div>
                  <div class="fleet-targets-list">
                    <Show when={(savedPresets() || []).length === 0}>
                      <div class="muted" style="padding:8px;">No saved presets yet. Check some targets, then “Save current…”.</div>
                    </Show>
                    <For each={savedPresets() || []}>
                      {(p) => (
                        <div class="fleet-preset-row">
                          <button class="fleet-preset-load" title={`Load ${p.pairs?.length || 0} target(s)`} onClick={() => applyPreset(p)}>
                            <span class="fleet-preset-name">{p.name}</span>
                            <span class="fleet-targets-count">{p.pairs?.length || 0}</span>
                          </button>
                          <button class="btn btn-sm btn-ghost fleet-preset-del" title="Delete preset" onClick={() => removePreset(p)}>🗑</button>
                        </div>
                      )}
                    </For>
                  </div>
                </div>
              </Show>
            </div>
          </Show>

          <button class="btn btn-sm" onClick={runAtCursor} disabled={running() || !sqlText().trim()}
                  title="Run statement under cursor (Ctrl+R)">
            ▶ Run
          </button>
          <button class="btn btn-sm btn-ghost" onClick={runAll} disabled={running() || !sqlText().trim()}
                  title={isPreset()
                    ? 'Run all (or selected) statements on every checked target (Ctrl+Shift+R)'
                    : 'Run all (or selected) statements, routing each to its [connection] (Ctrl+Shift+R)'}>
            ▶▶ Run all
          </button>
          <button class="btn btn-sm btn-ghost" onClick={runScript} disabled={running() || !sqlText().trim()}
                  title="Run as script — honours DELIMITER $$ … DELIMITER ; for stored procedures, triggers, and functions">
            ▷ Run script
          </button>
          <Show when={running()}>
            <button class="btn btn-sm btn-danger" onClick={stopQuery}
                    title="Cancel the running statement (KILL QUERY) and halt the run">
              ⏹ Stop
            </button>
          </Show>
          <Show when={defaultConn()}>
            <button class="btn btn-sm btn-ghost" onClick={() => openFindUsages()}
                    title="Find usages — search this identifier across the default connection's saved queries, scraps, history, forms, routines, triggers, and views.">
              🔍 Find usages
            </button>
          </Show>
          <button class="btn btn-sm btn-ghost" onClick={() => setDslHelpOpen(true)}
                  title="Query DSL & editor extras — the expand batch-generation statement, :parameters, editor snippets.">
            📖 DSL
          </button>

          <label class="checkbox-label" title="Wrap each connection's writes in a transaction — BEGIN before the first write to a connection, then Commit/Rollback per connection. Pure-read connections aren't wrapped. NOTE: DDL (CREATE/ALTER/DROP) implicitly commits in MySQL and escapes rollback.">
            <input type="checkbox" checked={txToggle()} onChange={(e) => setTxToggle(e.target.checked)} />
            Transaction
          </label>

          <span class="query-toolbar-sep" />

          <FormatMenu
            onPick={(mode) => { if (editorView) formatEditor(editorView, mode); }}
            autoFormat={editorAutoFormat()} onToggleAutoFormat={() => setEditorAutoFormat(!editorAutoFormat())}
            lineWrap={editorLineWrap()} onToggleLineWrap={() => setEditorLineWrap(!editorLineWrap())}
          />

          <div class="spacer" />
          <Show when={!isPreset()} fallback={
            <span class="muted mono" style="font-size:11px;">
              🎯 Preset — every statement runs on each checked target
            </span>
          }>
            <span class="muted mono" style="font-size:11px;">
              ⚡ Fleet — prefix a statement with <code class="mono">[connection]</code> to route it
            </span>
          </Show>
        </div>

        {/* Selected (connection / database) target chips — preset mode. */}
        <Show when={isPreset()}>
          <div class="fleet-targets-strip">
            <span class="fleet-tx-strip-label">Targets:</span>
            <Show when={presetTargets().length === 0}>
              <span class="muted" style="font-size:12px;">none — open 🎯 Targets to check connection + database pairs</span>
            </Show>
            <For each={presetTargets()}>
              {(t) => (
                <span class="fleet-target-chip">
                  {t.label}
                  <button class="fleet-target-chip-x" title="Remove target" onClick={() => removePair(t.conn.id, t.database)}>×</button>
                </span>
              )}
            </For>
          </div>
        </Show>

        {/* Open-transaction strip — one badge + Commit/Rollback per target
            with an uncommitted transaction; Commit/Rollback-all when several. */}
        <Show when={anyTxOpen()}>
          <div class="fleet-tx-strip">
            <span class="fleet-tx-strip-label">Uncommitted:</span>
            <For each={openTxTargets()}>
              {(t) => (
                <span class="fleet-tx-item">
                  <span class="query-tx-badge" title="Uncommitted changes hold locks until you Commit or Roll back">● {t.label}</span>
                  <button class="btn btn-sm" onClick={() => commitTx(t.key, t.label)}>✓ Commit</button>
                  <button class="btn btn-sm btn-danger" onClick={() => rollbackTx(t.key, t.label)}>↶ Rollback</button>
                </span>
              )}
            </For>
            <Show when={openTxTargets().length > 1}>
              <span class="query-toolbar-sep" />
              <button class="btn btn-sm" onClick={commitAll} title="Commit every open transaction">✓ Commit all</button>
              <button class="btn btn-sm btn-danger" onClick={rollbackAll} title="Roll back every open transaction">↶ Rollback all</button>
            </Show>
          </div>
        </Show>

        <div class="query-split">
          <div class="query-editor-wrap" classList={{ 'in-tx': anyTxOpen() }} style="flex-basis: 55%;">
            <CodeMirrorSQL
              value={sqlText()}
              onInput={onInput}
              onViewReady={(view) => { editorView = view; }}
              onContextMenu={onEditorContextMenu}
              autofocus={props.active}
              autoFormat={editorAutoFormat()}
              lineWrap={editorLineWrap()}
              bracketComplete={isPreset() ? undefined : bracketComplete}
              schema={sqlSchema}
              onExpandColumns={setExpandReq}
              height="100%"
            />
          </div>

          <div class="query-result" style="flex-basis: 45%;">
            <div class="query-result-tabs">
              <button class="inner-tab"
                      classList={{ active: activeResultTab() === 'messages' }}
                      onClick={() => setActiveResultTab('messages')}>
                Messages
                <Show when={messages.length > 0}>
                  <span class="inner-tab-badge"> {messages.length}</span>
                </Show>
              </button>
              <For each={resultTabs}>
                {(t) => (
                  <button
                    class="inner-tab inner-tab-result"
                    classList={{ active: activeResultTab() === t.key }}
                    onClick={() => setActiveResultTab(t.key)}
                    title={`${t.label} — ${t.rows.length} row${t.rows.length === 1 ? '' : 's'} in ${t.durationMs}ms${t.sql ? `\n\n${t.sql}` : ''}`}
                  >
                    <span class="inner-tab-label">{t.label}</span>
                    <span class="inner-tab-badge"> {formatNumberWithSep(t.rows.length)}</span>
                  </button>
                )}
              </For>
              <div class="spacer" />
              <Show when={activeResultTab() === 'messages' && messages.length > 0}>
                <button class="btn btn-sm btn-ghost" onClick={clearMessages}>Clear</button>
              </Show>
            </div>

            <div class="query-result-body">
              <Show when={activeResultTab() === 'messages'}>
                <div class="query-messages">
                  <Show when={messages.length === 0}>
                    <div class="muted" style="padding:16px;">No messages yet — run a query to see output.</div>
                  </Show>
                  {/* Newest first. */}
                  <For each={[...messages].reverse()}>
                    {(m) => (
                      <MessageRow
                        m={m}
                        now={nowTick()}
                        warnOpen={warnOpen()}
                        onToggleWarn={toggleWarn}
                        prefix={m.conn ? <span class="fleet-conn-chip" title="Connection this statement ran on">{m.conn}</span> : null}
                      />
                    )}
                  </For>
                </div>
              </Show>
              <Show when={activeResultTab() !== 'messages' && activeResult()}>
                <Grid
                  columns={activeResult().columns}
                  rows={activeResult().rows}
                  totalRows={activeResult().rows.length}
                  showTypes={gridShowTypes()}
                  thousandSep={gridThousandSep()}
                  selectable={true}
                />
              </Show>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Editor right-click context menu (rendered at click coords). */}
      <Show when={editorCtxMenu()}>
        <ContextMenu
          x={editorCtxMenu().x}
          y={editorCtxMenu().y}
          items={editorCtxMenu().items}
          onClose={() => setEditorCtxMenu(null)}
        />
      </Show>

      {/* Title-prompt: small modal that injects (or replaces) a
          `-- @name: <title>` annotation on the statement at the click. */}
      <Show when={titlePrompt()}>
        <Modal open={true} onClose={() => setTitlePrompt(null)} title="🏷 Result tab title">
          <p class="muted" style="font-size:12px;margin:0 0 12px;">
            <Show
              when={titlePrompt().existingName}
              fallback={<>Inserts <code class="mono">-- @name: …</code> above this statement so its message-log row carries the name. Visible to anyone reading the SQL — it's just a comment.</>}
            >
              Renames the statement. Empty value removes the title annotation.
            </Show>
          </p>
          <input
            ref={titleInputRef}
            class="input mono"
            style="width:100%;font-size:14px;"
            placeholder="e.g. Update fleet prices"
            value={titlePrompt().draft}
            onInput={(e) => setTitlePrompt({ ...titlePrompt(), draft: e.currentTarget.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); submitTitlePrompt(); }
              else if (e.key === 'Escape') { e.preventDefault(); setTitlePrompt(null); }
            }}
          />
          <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:14px;">
            <button class="btn btn-ghost" onClick={() => setTitlePrompt(null)}>Cancel</button>
            <button class="btn" onClick={submitTitlePrompt}>
              {titlePrompt().existingName
                ? (titlePrompt().draft.trim() === '' ? 'Remove' : 'Rename')
                : 'Add'}
            </button>
          </div>
        </Modal>
      </Show>

      {/* Paste-with-template modal — opened from the editor's right-click. */}
      <PasteTemplateModal
        open={!!pasteTemplateView()}
        onClose={() => setPasteTemplateView(null)}
        onPaste={onPasteTemplateConfirm}
      />

      {/* Find Usages — searches the default connection; every "open"
          destination loads the SQL into THIS editor (appended). */}
      <Show when={defaultConn()}>
        <FindUsagesModal
          open={findOpen()}
          onClose={() => setFindOpen(false)}
          connectionId={defaultConn()?.id}
          currentDatabase=""
          initialToken={findInitial()}
          onOpenSaved={async (id) => {
            try {
              const r = await api.get(`/api/saved-queries/${id}`);
              const q = r?.savedQuery || r;
              appendSql(q?.sqlText || '', 'saved query');
            } catch (e) {
              toast.error('Could not open saved query: ' + e.message);
            }
          }}
          onOpenScrap={async (id) => {
            try {
              const r = await api.get('/api/me/scraps');
              const sc = (r?.scraps || []).find((x) => x.id === id);
              if (sc) appendSql(sc.sqlText || '', 'scrap');
              else toast.error('Scrap not found (it may have been deleted)');
            } catch (e) {
              toast.error('Could not open scrap: ' + e.message);
            }
          }}
          onOpenHistory={(sql) => appendSql(sql, 'history entry')}
          onOpenSql={(sql, label) => appendSql(sql, label)}
        />
      </Show>

      {/* Ctrl+Shift+E — expand a table's columns at the cursor (resolved from
          the default connection's schema cache). */}
      <ColumnExpandModal
        open={!!expandReq()}
        request={expandReq()}
        connId={() => defaultConn()?.id}
        currentDb={() => ''}
        onClose={() => setExpandReq(null)}
      />

      {/* Parameter prompt — shown when a run contains :name parameters. */}
      <Show when={paramPrompt()}>
        <div class="modal-backdrop" onClick={cancelParams}>
          <div class="modal-card" style="width:min(520px,92vw);" onClick={(e) => e.stopPropagation()}>
            <h3>Query parameters</h3>
            <p class="muted" style="font-size:12px;margin:0 0 14px;">
              Enter a value for each parameter. Values are <strong>bound safely</strong> (sent as
              parameters, never pasted into the SQL) on <strong>every connection</strong> this run touches.
            </p>
            <For each={paramPrompt().params}>
              {(p, i) => (
                <div class="param-row">
                  <label class="field-label">
                    {p.name}
                    <Show when={p.type}><span class="muted mono"> #{p.type}#</span></Show>
                  </label>
                  <div class="param-input-row">
                    <Show
                      when={p.type === 'bool'}
                      fallback={
                        <input
                          class="input"
                          type={paramInputType(p.type)}
                          value={paramPrompt().values[p.name]}
                          disabled={paramPrompt().nulls[p.name]}
                          autofocus={i() === 0}
                          onInput={(e) => setParamValue(p.name, e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') submitParams(); }}
                        />
                      }
                    >
                      <select
                        class="input"
                        value={paramPrompt().values[p.name] || '1'}
                        disabled={paramPrompt().nulls[p.name]}
                        onChange={(e) => setParamValue(p.name, e.target.value)}
                      >
                        <option value="1">True (1)</option>
                        <option value="0">False (0)</option>
                      </select>
                    </Show>
                    <label class="checkbox-label param-null" title="Bind SQL NULL for this parameter">
                      <input
                        type="checkbox"
                        checked={paramPrompt().nulls[p.name]}
                        onChange={(e) => setParamNull(p.name, e.target.checked)}
                      />
                      NULL
                    </label>
                  </div>
                </div>
              )}
            </For>
            <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:16px;flex-wrap:wrap;">
              <button class="btn btn-ghost" onClick={cancelParams}>Cancel</button>
              <button class="btn" onClick={submitParams}>▶ Run</button>
            </div>
          </div>
        </div>
      </Show>

      {/* Safe-update guard — bare UPDATE/DELETE with no WHERE/LIMIT would touch
          every row on its target connection. */}
      <Show when={unsafePrompt()}>
        <div class="modal-backdrop" onClick={() => resolveUnsafe(false)}>
          <div class="modal-card" style="width:min(640px,92vw);" onClick={(e) => e.stopPropagation()}>
            <h3 style="color:var(--danger,#ef4444);">⚠ Affects every row</h3>
            <p class="muted" style="font-size:12px;margin:0 0 12px;">
              A bare <code>UPDATE</code>/<code>DELETE</code> would affect <strong>every row</strong> of
              its table — on the connection shown next to each statement.
            </p>
            <div class="unsafe-list">
              <For each={unsafePrompt().items}>
                {(it) => (
                  <div class="unsafe-item">
                    <span class={`unsafe-kind unsafe-${it.kind.toLowerCase()}`}>{it.kind}</span>
                    <span class="fleet-conn-chip">{it.conn}</span>
                    <code class="mono">{it.sql.length > 200 ? it.sql.slice(0, 200) + '…' : it.sql}</code>
                  </div>
                )}
              </For>
            </div>
            <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:16px;">
              <button class="btn btn-ghost" onClick={() => resolveUnsafe(false)} autofocus>Cancel</button>
              <button class="btn btn-danger" onClick={() => resolveUnsafe(true)}>Run anyway</button>
            </div>
          </div>
        </div>
      </Show>

      {/* Implicit-commit guard — shown when a statement would commit its target
          connection's open transaction. */}
      <Show when={commitPrompt()}>
        <div class="modal-backdrop" onClick={() => resolveCommit(false)}>
          <div class="modal-card" style="width:min(560px,92vw);" onClick={(e) => e.stopPropagation()}>
            <h3 style="color:var(--warn,#f59e0b);">⚠ This will commit an open transaction</h3>
            <p class="muted" style="font-size:12px;margin:0 0 12px;">
              The connection shown next to each statement has an <strong>open transaction</strong>.
              MySQL automatically commits it before {commitPrompt().items.length === 1 ? 'this statement' : 'these statements'},
              and the commit <strong>cannot be rolled back</strong>:
            </p>
            <div class="unsafe-list">
              <For each={commitPrompt().items}>
                {(it) => (
                  <div class="unsafe-item">
                    <span class="commit-kind">{it.kind}</span>
                    <span class="fleet-conn-chip">{it.conn}</span>
                    <code class="mono">{it.sql.length > 200 ? it.sql.slice(0, 200) + '…' : it.sql}</code>
                  </div>
                )}
              </For>
            </div>
            <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:16px;">
              <button class="btn btn-ghost" onClick={() => resolveCommit(false)} autofocus>Cancel</button>
              <button class="btn btn-danger" onClick={() => resolveCommit(true)}>Commit &amp; run</button>
            </div>
          </div>
        </div>
      </Show>

      {/* Save query dialog (sidebar + Save) — same shape as the query tab's, so
          fleet queries land in the SAME shared saved-queries list. */}
      <Show when={savingDlg()}>
        <div class="modal-backdrop" onClick={closeSaveDialog}>
          <div class="modal-card" style="width:min(640px,92vw);" onClick={(e) => e.stopPropagation()}>
            <h3>{savingDlg().editingId ? 'Edit saved query' : 'Save query'}</h3>
            <label class="field-label">Title</label>
            <input class="input" value={savingDlg().title}
                   onInput={(e) => setSavingDlg({ ...savingDlg(), title: e.target.value })}
                   placeholder="e.g. Fleet health check" autofocus />
            <label class="field-label" style="margin-top:10px;">Description (optional)</label>
            <textarea class="input" rows="3" value={savingDlg().description}
                      onInput={(e) => setSavingDlg({ ...savingDlg(), description: e.target.value })}
                      placeholder="What does this query do?" />
            <label class="field-label" style="margin-top:10px;">Source database (informational)</label>
            <input class="input mono" value={savingDlg().sourceDatabase}
                   onInput={(e) => setSavingDlg({ ...savingDlg(), sourceDatabase: e.target.value })}
                   placeholder="e.g. dbfastretail" />
            <label class="checkbox-label" style="margin-top:12px;">
              <input type="checkbox" checked={savingDlg().shared}
                     onChange={(e) => setSavingDlg({ ...savingDlg(), shared: e.currentTarget.checked })} />
              <span>Share with tagged users</span>
            </label>
            <Show when={savingDlg().shared}>
              <div class="muted" style="font-size:12px;margin:4px 0;">Read-only for users who have any of these tags:</div>
              <Show when={(tagOptions() || []).length}
                    fallback={<div class="muted" style="font-size:12px;">No tags exist yet — an admin assigns tags to users on the Users page.</div>}>
                <div class="sq-tag-picker">
                  <For each={tagOptions()}>
                    {(t) => (
                      <label class="checkbox-label">
                        <input type="checkbox" checked={(savingDlg().shareTags || []).includes(t)} onChange={() => toggleShareTag(t)} />
                        <span>{t}</span>
                      </label>
                    )}
                  </For>
                </div>
              </Show>
            </Show>
            <label class="field-label" style="margin-top:10px;">Query content</label>
            <div class="saved-query-editor">
              <CodeMirrorSQL value={savingDlg().sqlText} onInput={(v) => setSavingDlg({ ...savingDlg(), sqlText: v })}
                             height="220px" placeholder="SELECT …" schema={sqlSchema} />
            </div>
            <div style="display:flex;gap:8px;margin-top:14px;justify-content:flex-end;">
              <button class="btn btn-ghost" onClick={closeSaveDialog}>Cancel</button>
              <button class="btn" onClick={commitSave}>Save</button>
            </div>
          </div>
        </div>
      </Show>

      {/* Load: append-or-replace prompt (only when the editor isn't empty) */}
      <Show when={loadPrompt()}>
        <div class="modal-backdrop">
          <div class="modal-card" style="width:min(440px,92vw);">
            <h3>Load {loadPrompt().label}</h3>
            <p class="muted" style="margin:6px 0 14px;">
              The editor already has content. Append the loaded query below it, or replace everything?
            </p>
            <div style="display:flex;gap:8px;justify-content:flex-end;">
              <button class="btn btn-ghost" onClick={() => setLoadPrompt(null)}>Cancel</button>
              <button class="btn" onClick={() => { applyLoad(loadPrompt().sql, 'append'); logInfo(`Appended ${loadPrompt().label}`); setLoadPrompt(null); }}>Append</button>
              <button class="btn btn-danger" onClick={() => { applyLoad(loadPrompt().sql, 'replace'); logInfo(`Replaced editor with ${loadPrompt().label}`); setLoadPrompt(null); }}>Replace</button>
            </div>
          </div>
        </div>
      </Show>

      {/* 📖 DSL — query-DSL / editor-extras help */}
      <QueryDslHelp open={dslHelpOpen()} onClose={() => setDslHelpOpen(false)} />
    </div>
  );
}
